// bulk-tag-dialog — imperative "Tag N selected entities" UX.
//
// CHG-1330 / ITER-3457 — closes DRIFT-648 bulk-tag entity-list flow.
//
// Surface:
//   - `openBulkTagDialog({ entityType, ids })` — imperative call from a
//     ListPage bulkActions descriptor's onAction. Pops a small modal
//     containing a TagPicker; user selects 1+ tags + clicks Apply.
//   - `<BulkTagDialogHost />` — mount once at App root. Listens to the
//     module-level emitter and renders the modal when a request is
//     pending. Renders null when idle.
//
// On Apply: loops attachTag(entityType, id, tagId) for the cross-product
// of selected entities × selected tags. Reports per-tag counts and any
// errors inline. Invalidates the entity's react-query keys + the tag
// lists so all visible chips refresh.
//
// Why imperative instead of a controlled prop: ListPage doesn't want to
// own the "is a tag dialog open?" state — that's a transient cross-
// cutting UX. The singleton listener is the only React-component scope
// that needs to track it, and a single host at the app root keeps the
// modal at the correct z-index without each list page needing its own
// portal.

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { attachTag, listTags, type TaggableEntityType } from "../api/tags";
import { TagPicker } from "../components/composition/TagPicker";

export interface BulkTagRequest {
  entityType: TaggableEntityType;
  ids: Array<string | number>;
}

// ─────────────────────────────────────────────────────────────────
// Emitter: a tiny singleton the host subscribes to. Multiple hosts
// would race; the contract is "mount exactly one BulkTagDialogHost".
// ─────────────────────────────────────────────────────────────────

type Listener = (req: BulkTagRequest | null) => void;
let activeListener: Listener | null = null;

export function openBulkTagDialog(req: BulkTagRequest): void {
  if (!activeListener) {
    // Pure no-op if no host is mounted. Avoids dev-time foot-guns where
    // someone calls open() before the provider mounts.
    return;
  }
  activeListener(req);
}

// ─────────────────────────────────────────────────────────────────
// Host component — mount once near the App root.
// ─────────────────────────────────────────────────────────────────

export function BulkTagDialogHost() {
  const [req, setReq] = useState<BulkTagRequest | null>(null);

  useEffect(() => {
    activeListener = setReq;
    return () => {
      activeListener = null;
    };
  }, []);

  if (!req) return null;
  return <BulkTagDialog request={req} onClose={() => setReq(null)} />;
}

// ─────────────────────────────────────────────────────────────────
// Dialog content — fixed-position overlay + card with TagPicker.
// ─────────────────────────────────────────────────────────────────

interface BulkTagDialogProps {
  request: BulkTagRequest;
  onClose: () => void;
}

type Phase =
  | { kind: "idle" }
  | { kind: "tagging" }
  | { kind: "done"; attached: number; failed: number };

function BulkTagDialog({ request, onClose }: BulkTagDialogProps) {
  const qc = useQueryClient();
  const [pendingTagIds, setPendingTagIds] = useState<string[]>([]);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const allTagsQ = useQuery({
    queryKey: ["tags", "list", "all-for-picker"] as const,
    queryFn: () => listTags({ pageSize: 200 }),
  });
  const allTags = useMemo(() => allTagsQ.data?.items ?? [], [allTagsQ.data]);

  function handlePickerAttach(tagId: string) {
    setPendingTagIds((prev) => (prev.includes(tagId) ? prev : [...prev, tagId]));
  }
  function handlePickerDetach(tagId: string) {
    setPendingTagIds((prev) => prev.filter((t) => t !== tagId));
  }

  async function applyTags() {
    setPhase({ kind: "tagging" });
    let attached = 0;
    let failed = 0;
    for (const entityId of request.ids) {
      for (const tagId of pendingTagIds) {
        try {
          await attachTag(request.entityType, String(entityId), tagId);
          attached += 1;
        } catch {
          failed += 1;
        }
      }
    }
    // Invalidate the affected entity lists + per-entity tag queries so
    // newly-attached chips appear. Cast keys broadly — react-query
    // matches by prefix.
    qc.invalidateQueries({ queryKey: ["tags"] });
    qc.invalidateQueries({ queryKey: [`${request.entityType}s`] });
    setPhase({ kind: "done", attached, failed });
  }

  const tagPlural = pendingTagIds.length === 1 ? "tag" : "tags";
  const entityPlural = request.ids.length === 1 ? request.entityType : `${request.entityType}s`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Tag selected entities"
      data-testid="bulk-tag-dialog"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && phase.kind !== "tagging") onClose();
      }}
    >
      <div
        className="card"
        style={{
          width: "min(440px, 92vw)",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          background: "var(--color-surface)",
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            Tag {request.ids.length} selected {entityPlural}
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>
            Pick one or more tags to attach to every selected row.
          </div>
        </div>

        <TagPicker
          allTags={allTags}
          selectedTagIds={pendingTagIds}
          onAttach={handlePickerAttach}
          onDetach={handlePickerDetach}
          placeholder="Search or add a tag…"
          disabled={phase.kind === "tagging" || phase.kind === "done"}
        />

        {phase.kind === "done" ? (
          <div
            role="status"
            data-testid="bulk-tag-status"
            style={{
              fontSize: 13,
              color: phase.failed > 0 ? "var(--color-error)" : "var(--color-text)",
            }}
          >
            Tagged {phase.attached} {phase.attached === 1 ? "row" : "rows"} with{" "}
            {pendingTagIds.length} {tagPlural}
            {phase.failed > 0 ? ` (${phase.failed} failed)` : ""}.
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={phase.kind === "tagging"}
          >
            {phase.kind === "done" ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={applyTags}
            disabled={
              pendingTagIds.length === 0 ||
              phase.kind === "tagging" ||
              phase.kind === "done"
            }
            data-testid="bulk-tag-apply"
          >
            {phase.kind === "tagging" ? "Tagging…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
